/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext, IDateFormatOptions /*, getUrlSync*/ } from '@msdyn365-commerce/core';
// import { SalesInvoiceLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IInvoiceErrorState /*, InvoiceErrorComponent*/ } from '@msdyn365-commerce-modules/invoice';
// import { getSimpleProductsAction, ProductInput } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { InvoiceDetailsHeaderComponent } from './components/gdn-invoice-details-header';
import { InvoiceDetailsPaymentDetailsComponent } from './components/gdn-invoice-details-payment-summary';
import { InvoiceDetailsProductsTableComponent, IProductsTableComponentResources } from './components/gdn-invoice-details-products-table';
// import { InvoiceDetailsModel } from './helpers/invoice-data';
import { IGdnInvoiceDetailsData } from './gdn-invoice-details.data';
import { IGdnInvoiceDetailsProps } from './gdn-invoice-details.props.autogenerated';
import { IGetGdnInvoiceDetailsResponse } from '../../actions/DataServiceEntities.g';

export interface IInvoiceDetailsViewState {
    // invoiceDetails?: InvoiceDetailsModel;
    // payInvoiceErrorModalOpen?: boolean;
    errorState?: IInvoiceErrorState;
    gdnInvoiceDetails?: IGetGdnInvoiceDetailsResponse;
}

export interface IInvoiceDetailsViewProps extends IGdnInvoiceDetailsProps<IGdnInvoiceDetailsData> {
    moduleProps: IModuleProps;
    header: JSX.Element;
    products: JSX.Element;
    paymentDetails: JSX.Element;
    shipmentDetails: JSX.Element;
    // payInvoiceError?: JSX.Element;
    invoiceDetailsError?: JSX.Element;
}

export const formatShortDate = (context: ICoreContext, date: Date | undefined) => {
    if (date === undefined) {
        return '';
    }
    const dateOptions: IDateFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
    const newDate = new Date(date);
    // Convert time to UTC time in order to match generated invoices
    newDate.setTime(newDate.getTime() + newDate.getTimezoneOffset() * 60000);
    return context.cultureFormatter.formatDate(newDate.toUTCString(), dateOptions);
};

export const formatAmount = (context: ICoreContext, amount: number | undefined, currencyCode: string | undefined) => {
    if (amount === undefined) {
        return '';
    }
    return context.cultureFormatter.formatCurrency(amount, currencyCode);
};

const CURRENCY_CODE = 'USD';

/**
 * InvoiceDetails component.
 * @extends {React.PureComponent<IGdnInvoiceDetailsProps<IGdnInvoiceDetailsData>>}
 */
class InvoiceDetails extends React.PureComponent<IGdnInvoiceDetailsProps<IGdnInvoiceDetailsData>, IInvoiceDetailsViewState> {
    private static readonly _className: string = 'msc-invoice-details';

    public constructor(props: IGdnInvoiceDetailsProps<IGdnInvoiceDetailsData>) {
        super(props);

        this.state = { errorState: { errorMessage: '', otherError: '' } };
        // this._onAddToCartFailed = this._onAddToCartFailed.bind(this);
        // this._setPayInvoiceErrorModalClose = this._setPayInvoiceErrorModalClose.bind(this);
    }

    public async componentDidMount(): Promise<void> {
        // const invoice = await this.props.data.invoice;
        // const invoiceDetails = await this.props.data.invoiceDetails;
        const gdnInvoiceDetails = await this.props.data.gdnInvoiceDetails;

        // const products = await this._getProducts(invoiceDetails.SalesInvoiceLine, invoiceDetails.ChannelId);

        // const invoiceDetailsModel = new InvoiceDetailsModel(invoice, invoiceDetails, products);
        this.setState({
            // invoiceDetails: invoiceDetailsModel,
            // payInvoiceErrorModalOpen: false,
            errorState: { errorMessage: '', otherError: '' },
            gdnInvoiceDetails: gdnInvoiceDetails
        });
    }

    public render(): JSX.Element | null {
        if (!this.state.gdnInvoiceDetails /* && !this.state.invoiceDetails */) {
            return null;
        }

        const header = this._renderHeader();
        const products = this._renderProducts();
        const paymentDetails = this._renderPaymentDetails();
        // const payInvoiceError = this._renderInvoiceErrors();
        const shipmentDetails = this._renderShipmentInformation();
        const invoiceDetailsError = !this.state.gdnInvoiceDetails?.ErrorMessage ? (
            undefined
        ) : (
            <div className={`${InvoiceDetails._className}__no-details-error`}>{this.props.resources.noInvoiceDetailsFoundErrorMessage}</div>
        );

        const props: IInvoiceDetailsViewProps = {
            ...(this.props as IGdnInvoiceDetailsProps<IGdnInvoiceDetailsData>),
            moduleProps: {
                moduleProps: this.props,
                className: InvoiceDetails._className
            },
            header,
            products,
            paymentDetails,
            shipmentDetails,
            // payInvoiceError,
            invoiceDetailsError
        };

        return this.props.renderView(props);
    }

    // private async _getProducts(invoiceLines: SalesInvoiceLine[] = [], channelId?: number): Promise<SimpleProduct[]> {
    //     const productInputs = invoiceLines
    //         .filter(product => product.ProductId)
    //         .map(
    //             product =>
    //                 new ProductInput(
    //                     product.ProductId!,
    //                     this.props.context.request.apiSettings,
    //                     channelId,
    //                     undefined,
    //                     this.props.context.actionContext.requestContext
    //                 )
    //         );
    //     return getSimpleProductsAction(productInputs, this.props.context.actionContext);
    // }

    private _renderHeader(): JSX.Element {
        // const invoiceDetails = this.state.invoiceDetails!;
        const gdnInvoiceDetails = this.state.gdnInvoiceDetails!;
        return (
            <InvoiceDetailsHeaderComponent
                className={`${InvoiceDetails._className}__header`}
                id={`${InvoiceDetails._className}__test`}
                // id={`${InvoiceDetails._className}__${invoiceDetails.id}`}
                data={{ /*invoiceDetails,*/ gdnInvoiceDetails }}
                typeName={this.props.typeName}
                context={this.props.context}
                resources={this.props.resources}
                // onError={this._onAddToCartFailed}
            />
        );
    }

    private _renderProducts(): JSX.Element {
        const {
            itemIdHeading,
            descriptionHeading,
            quantityHeading,
            listPriceHeading,
            unitPriceHeading,
            totalHeading,
            custItemNumLabel
        } = this.props.resources;
        const gdnInvoiceDetails = this.state.gdnInvoiceDetails!;
        const productsTableResources: IProductsTableComponentResources = {
            itemIdHeading: itemIdHeading,
            descriptionHeading: descriptionHeading,
            quantityHeading: quantityHeading,
            listPriceHeading: listPriceHeading,
            unitPriceHeading: unitPriceHeading,
            totalHeading: totalHeading,
            custItemNumLabel: custItemNumLabel
        };
        return (
            <InvoiceDetailsProductsTableComponent
                className={InvoiceDetails._className}
                currencyCode={CURRENCY_CODE}
                ctx={this.props.context}
                resources={productsTableResources}
                invoiceDetails={gdnInvoiceDetails.InvoiceDetails}
                invoiceLines={gdnInvoiceDetails.InvoiceLineDetails}
            />
        );
    }

    private _renderPaymentDetails(): JSX.Element {
        // const invoiceDetails = this.state.invoiceDetails!;
        const gdnInvoiceDetails = this.state.gdnInvoiceDetails!;
        return (
            <>
                <InvoiceDetailsPaymentDetailsComponent
                    className={`${InvoiceDetails._className}__payment-details`}
                    currencyCode={CURRENCY_CODE}
                    // id={`${InvoiceDetails._className}__${invoiceDetails.id}`}
                    id={`${InvoiceDetails._className}__test1`}
                    data={{ gdnInvoiceDetails }}
                    typeName={this.props.typeName}
                    context={this.props.context}
                    resources={this.props.resources}
                />
            </>
        );
    }

    private _renderShipmentInformation(): JSX.Element {
        const {
            shippingInformationHeadingText,
            containerIdLabelText,
            weightLabelText,
            shippingCarrierLabelText,
            trackingLabel,
            quantityHeading
        } = this.props.resources;
        const gdnInvoiceDetails = this.state.gdnInvoiceDetails!;
        const shippingInformation = gdnInvoiceDetails.ShippingContainerInformation;

        if (!shippingInformation || shippingInformation.length === 0) {
            return <></>;
        }

        return (
            <>
                <div className={`${InvoiceDetails._className}__shipment-header`}>{shippingInformationHeadingText}</div>
                {shippingInformation.map((c, index) => {
                    return (
                        <div key={`shipping-${index}`} className={`${InvoiceDetails._className}__shipment`}>
                            <div className={`${InvoiceDetails._className}__shipment--details`}>
                                <span className={`${InvoiceDetails._className}__shipment--details__info`}>
                                    {containerIdLabelText}
                                    {c.ContainerId}
                                </span>
                                <span className={`${InvoiceDetails._className}__shipment--details__info`}>
                                    {weightLabelText}
                                    {c.Weight}
                                </span>
                                <span className={`${InvoiceDetails._className}__shipment--details__info`}>
                                    {shippingCarrierLabelText}
                                    {c.ShippingCarrier}
                                </span>
                                <span className={`${InvoiceDetails._className}__shipment--details__info`}>
                                    {trackingLabel}
                                    {c.TrackingNumber}
                                </span>
                            </div>
                            <div key={`shipping-${index}`} className={`${InvoiceDetails._className}__shipment--line-details`}>
                                {c.ContainerLinesList?.map((cLines, index) => (
                                    <div
                                        key={`containerLines-${index}`}
                                        className={`${InvoiceDetails._className}__shipment--line-details-row`}
                                    >
                                        {`${cLines.ItemId} - ${quantityHeading}: ${cLines.Quantity}`}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </>
        );
    }

    // private _onAddToCartFailed(newErrorState: IInvoiceErrorState): void {
    //     const errorState = { ...newErrorState };
    //     errorState.errorHost = 'ADDINVOICETOCART';

    //     // check for errorcode and otherError which contains the LocalizedMessage from API
    //     if (
    //         errorState &&
    //         errorState.errorCode === 'Microsoft_Dynamics_Commerce_Runtime_InvoiceLineAlreadyExistsInCart' &&
    //         errorState.otherError
    //     ) {
    //         this.setState({ errorState: errorState });
    //     }

    //     this.setState({ payInvoiceErrorModalOpen: true });
    // }

    // private _setPayInvoiceErrorModalClose(): void {
    //     this.setState({ payInvoiceErrorModalOpen: false });
    // }

    // private _renderInvoiceErrors(): JSX.Element {
    //     const {
    //         resources: {
    //             payInvoiceErrorDialogHeader,
    //             payInvoiceErrorDialogBodyErrorTitle,
    //             payInvoiceErrorDialogBodyErrorDescription,
    //             payInvoiceErrorDialogFooterGoToCartButtonText,
    //             payInvoiceErrorDialogFooterCancelButtonText
    //         }
    //     } = this.props;

    //     const cartUrl = getUrlSync('cart', this.props.context.actionContext) || '';

    //     const errorClassName = `${InvoiceDetails._className}__errors`;

    //     return (
    //         <InvoiceErrorComponent
    //             id={errorClassName}
    //             typeName={this.props.typeName}
    //             data={{}}
    //             context={this.props.context}
    //             payInvoiceErrorModalOpen={this.state.payInvoiceErrorModalOpen || false}
    //             showError={false}
    //             errorDialogHeader={payInvoiceErrorDialogHeader}
    //             errorDialogBodyErrorTitle={payInvoiceErrorDialogBodyErrorTitle}
    //             // show otherError (localizedMessage) from API failure to dialog box
    //             errorDialogBodyErrorDescription={this.state.errorState?.otherError || payInvoiceErrorDialogBodyErrorDescription}
    //             errorDialogFooterGoToCartButtonText={payInvoiceErrorDialogFooterGoToCartButtonText}
    //             errorDialogFooterCancelButtonText={payInvoiceErrorDialogFooterCancelButtonText}
    //             cartUrl={cartUrl}
    //             setPayInvoiceErrorModalClose={this._setPayInvoiceErrorModalClose}
    //         />
    //     );
    // }
}

export default InvoiceDetails;
